import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Stethoscope, Mail, Building2, ArrowLeft, CheckCircle2, Loader2, AlertCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Kept in sync with backend GENERIC_EMAIL_DOMAINS. Used only to toggle the
// declared-institution field client-side; the source of truth is the backend.
const GENERIC_EMAIL_DOMAINS = new Set([
  "gmail.com", "googlemail.com", "hotmail.com", "hotmail.es", "hotmail.com.ar",
  "outlook.com", "outlook.es", "live.com", "live.com.ar", "msn.com",
  "yahoo.com", "yahoo.com.ar", "yahoo.es", "ymail.com", "rocketmail.com",
  "icloud.com", "me.com", "mac.com",
  "protonmail.com", "proton.me", "pm.me",
  "aol.com", "gmx.com", "gmx.net", "mail.com", "zoho.com", "yandex.com",
]);

const isGenericDomain = (email) => {
  if (!email || !email.includes("@")) return false;
  const domain = email.split("@")[1]?.trim().toLowerCase();
  return GENERIC_EMAIL_DOMAINS.has(domain);
};

const DoctorRegister = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [declaredInstitution, setDeclaredInstitution] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [routingResult, setRoutingResult] = useState(null);

  const showDeclaredField = useMemo(() => isGenericDomain(email), [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error("Ingresa tu email");
      return;
    }

    setLoading(true);
    try {
      const body = { email };
      if (showDeclaredField && declaredInstitution.trim()) {
        body.declared_institution = declaredInstitution.trim();
      }

      const response = await fetch(`${API_URL}/api/auth/doctor/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok) {
        setRoutingResult({
          path: data.routing_path,
          primary: data.primary_institution_name,
        });
        setSubmitted(true);
        toast.success("Revisa tu email para continuar");
      } else {
        toast.error(data.detail || "Error al registrar");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    const isCorporateMatch = routingResult?.path === "A";
    const brand = routingResult?.primary || "el portal";

    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">¡Revisa tu email!</h2>
            <p className="text-slate-600 mb-4">
              Te enviamos un enlace de verificación a <strong>{email}</strong>
            </p>

            {isCorporateMatch && (
              <div
                className="p-3 mb-3 bg-blue-50 border border-blue-300 rounded-lg flex items-start gap-2.5 text-left"
                data-testid="corporate-match-notice"
              >
                <Building2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-900">
                  Detectamos tu dominio corporativo. Vas a tener acceso al portal de{" "}
                  <strong>{brand}</strong> junto con tu Consultorio Privado.
                </p>
              </div>
            )}

            <p className="text-sm text-slate-500 mb-4">
              El enlace expira en 24 horas.
            </p>
            <div
              className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2.5 text-left"
              data-testid="spam-alert"
            >
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                <strong>¿No te llegó?</strong> Revisá tu carpeta de <strong>spam o correo no deseado</strong>.
              </p>
            </div>

            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mt-6"
              data-testid="register-back-home-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al inicio
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-[#1d366b] text-white p-6 text-center">
            <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Stethoscope className="h-7 w-7" />
            </div>
            <h1 className="text-2xl font-bold">Solicitar Acceso</h1>
            <p className="text-slate-300 text-sm mt-1">Registro para profesionales médicos</p>
          </div>

          <div className="p-6">
            <p className="text-slate-600 text-sm mb-6">
              Registrate para atender consultas asincrónicas. Vas a tener tu Consultorio Privado
              y, si trabajás en una institución con acuerdo activo, acceso a su portal también.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email profesional</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu.email@ejemplo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="doctor-email-input"
                  />
                </div>
                <p className="mt-1.5 text-xs text-slate-500">
                  Si trabajás en una institución con acuerdo, usá tu email corporativo.
                </p>
              </div>

              {showDeclaredField && (
                <div data-testid="declared-institution-block" className="animate-in fade-in slide-in-from-top-2 duration-200">
                  <Label htmlFor="declared_institution">
                    ¿Vas a atender pacientes de alguna institución específica?{" "}
                    <span className="text-slate-400 font-normal">(opcional)</span>
                  </Label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="declared_institution"
                      type="text"
                      placeholder="Ej: Sanatorio Santa Fe, OSDE, etc."
                      value={declaredInstitution}
                      onChange={(e) => setDeclaredInstitution(e.target.value)}
                      className="pl-10"
                      maxLength={120}
                      data-testid="declared-institution-input"
                    />
                  </div>
                  <div className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-500">
                    <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      Detectamos que usás un email personal. Si además atendés por una institución,
                      indicá su nombre y nuestro equipo valida el vínculo. Si no, dejalo en blanco y
                      arrancás solo con tu Consultorio Privado.
                    </span>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="doctor-register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  "Continuar con registro"
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                data-testid="register-back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorRegister;
